import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, Zap, HelpCircle, Settings, Menu, X,
  Moon, Sun, Command, LogOut, User, Database, MessageCircle, List, Activity, Smartphone
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import ModernToolbar from './ModernToolbar';
import ShortcutsModal from './ShortcutsModal';
import toast from 'react-hot-toast';

const Layout: React.FC = () => {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const isEditor = location.pathname === '/editor';

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Connetti WhatsApp', href: '/connect', icon: Smartphone },
    { name: 'Automazioni', href: '/flows', icon: List },
    { name: 'Editor', href: '/editor', icon: Zap },
    { name: 'Attività', href: '/activity', icon: Activity },
    { name: 'Test API', href: '/test', icon: MessageCircle },
    { name: 'Test Integrazione', href: '/integration', icon: Zap },
    { name: 'Aiuto', href: '/help', icon: HelpCircle },
    { name: 'Impostazioni', href: '/settings', icon: Settings },
  ];
  
  // Aggiungi DataViewer in modalità sviluppo
  if (process.env.NODE_ENV === 'development') {
    navigation.push({ name: 'Database', href: '/data', icon: Database });
  }

  const isActive = (path: string) => location.pathname === path;


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Navbar */}
      <nav className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border fixed top-0 w-full z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side */}
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 ml-2 lg:ml-0">
                <div className="">
                  <img src="/caffesempione.png" alt="Caffè Sempione" className="w-full" style={{ height: '54px' }} />
                </div>
                {/* <div className="hidden sm:flex flex-col">
                  <span className="text-lg font-bold text-caffe-primary dark:text-caffe-accent">
                    Caffè Sempione
                  </span>
                  <span className="text-xs text-caffe-secondary dark:text-caffe-light -mt-1">
                    WhatsApp Business
                  </span>
                </div> */}
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-2 ml-10">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white shadow-md'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Shortcuts */}
              <button
                onClick={() => setShowShortcuts(true)}
                className="hidden sm:flex items-center gap-2 p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Command className="w-5 h-5" />
              </button>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-whatsapp-green to-emerald-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">MR</span>
                  </div>
                </button>

                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border overflow-hidden"
                  >
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User className="w-4 h-4" />
                      <span>Profilo</span>
                    </Link>
                    <button
                      onClick={() => {
                        toast.success('Disconnessione effettuata');
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left text-red-600 dark:text-red-400"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Esci</span>
                    </button>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        >
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            className="fixed left-0 top-0 bottom-0 w-72 bg-white dark:bg-dark-surface shadow-xl"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className="p-4">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-whatsapp-green to-emerald-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl font-bold">W</span>
                </div>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  WhatsApp Flow
                </span>
              </div>

              <nav className="space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white shadow-md'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Main content */}
      <main className={`${isEditor ? 'pt-16' : 'pt-16'}`}>
        {isEditor ? (
          <div className="h-[calc(100vh-4rem)]">
            <ModernToolbar
              onShowShortcuts={() => setShowShortcuts(true)}
            />
            <div className="h-[calc(100%-4rem)]">
              <Outlet />
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {/* Shortcuts Modal */}
      <ShortcutsModal
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />
    </div>
  );
};

export default Layout;
